/**
 * 密码编码工具函数
 * Password Encoding Utilities
 */

/**
 * 将密码进行Base64编码
 * @param {string} password - 原始密码
 * @returns {string} - Base64编码后的密码
 */
export const encodePassword = (password) => {
  try {
    // 处理Unicode字符，确保正确编码
    const utf8Password = unescape(encodeURIComponent(password));
    return btoa(utf8Password);
  } catch (error) {
    console.error('Password encoding error:', error);
    // 如果编码失败，返回原密码（降级处理）
    return password;
  }
};

/**
 * 将Base64编码的密码解码
 * @param {string} encodedPassword - Base64编码的密码
 * @returns {string} - 解码后的原始密码
 */
export const decodePassword = (encodedPassword) => {
  try {
    const decodedBytes = atob(encodedPassword);
    return decodeURIComponent(escape(decodedBytes));
  } catch (error) {
    console.error('Password decoding error:', error);
    // 如果解码失败，返回原密码（降级处理）
    return encodedPassword;
  }
};

/**
 * 检查字符串是否为有效的Base64编码
 * @param {string} str - 要检查的字符串
 * @returns {boolean} - 是否为有效的Base64编码
 */
export const isValidBase64 = (str) => {
  try {
    // Base64字符集正则表达式
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    
    if (!base64Regex.test(str)) {
      return false;
    }
    
    // 尝试解码来验证有效性
    atob(str);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 安全的密码传输包装函数
 * @param {string} password - 原始密码
 * @returns {object} - 包含编码密码和标记的对象
 */
export const preparePasswordForTransmission = (password) => {
  const encodedPassword = encodePassword(password);
  
  return {
    password: encodedPassword,
    encoded: true,
    timestamp: Date.now() // 添加时间戳用于调试
  };
};

/**
 * 生成随机salt（预留扩展功能）
 * @param {number} length - salt长度
 * @returns {string} - 随机salt字符串
 */
export const generateSalt = (length = 16) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
};

/**
 * 密码强度检查（预留扩展功能）
 * @param {string} password - 要检查的密码
 * @returns {object} - 密码强度信息
 */
export const checkPasswordStrength = (password) => {
  const result = {
    score: 0,
    feedback: [],
    isStrong: false
  };

  if (password.length >= 8) {
    result.score += 1;
  } else {
    result.feedback.push('密码长度至少8位');
  }

  if (/[a-z]/.test(password)) {
    result.score += 1;
  } else {
    result.feedback.push('包含小写字母');
  }

  if (/[A-Z]/.test(password)) {
    result.score += 1;
  } else {
    result.feedback.push('包含大写字母');
  }

  if (/[0-9]/.test(password)) {
    result.score += 1;
  } else {
    result.feedback.push('包含数字');
  }

  if (/[^a-zA-Z0-9]/.test(password)) {
    result.score += 1;
  } else {
    result.feedback.push('包含特殊字符');
  }

  result.isStrong = result.score >= 4;
  
  return result;
};
